import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useIsDarkTheme, useTheme } from '../../hooks/useTheme';
import { useContentWidth, WIDTH_CLASSES } from '../../hooks/useContentWidth';
import { supabase } from '../../lib/supabase';
import { embedOne } from '../../lib/utils';
import { isConversationUnread, markConversationSeen } from '../../lib/conversationSeen';
import { clearPendingTryChat } from '../../lib/tryChatHandoff';
import { BottomSheet, FloatingTabBar, IconButton, NavItem, Sidebar } from '../design-system';
import type { FloatingTabBarItem, SidebarDetailRow, SidebarNavItem } from '../design-system';
import { AccountMenu } from './AccountMenu';
import { KeyboardCollapse } from './KeyboardCollapse';
import { FaqModal } from '../FaqModal';
import { SupportModal } from '../SupportModal';
import { FeedbackPopover } from '../FeedbackPopover';
import { NotificationsPopover } from './NotificationsPopover';
import type { MatchStatus, Message } from '../../types';

interface TripMeta {
  tripName: string | null;
  destination: string | null;
  status: MatchStatus;
  hostName: string | null;
  dayDates: string[];
  hostId: string | null;
  conversationId: string | null;
}

interface TripOption {
  matchId: string;
  label: string;
  status: MatchStatus;
  /** The trip this host belongs to, and (for a trip with several cities) the city and host. */
  portraitId?: string | null;
  legCity?: string | null;
  legPosition?: number | null;
  hostFirst?: string;
}

/**
 * A trip with several cities has a host, and a chat, per city. Lists the cities
 * of the trip the traveler is looking at, in trip order, so they can switch
 * between hosts without going back to the trip list. Only cities that are
 * booked (paid) show; none when the trip is one block.
 */
export function citySwitchOptions(trips: TripOption[], currentMatchId: string | undefined): TripOption[] {
  const current = trips.find((t) => t.matchId === currentMatchId);
  if (!current?.portraitId || !current.legCity) return [];
  const cities = trips
    .filter((t) => t.portraitId === current.portraitId && t.legCity && (t.status === 'pending' || t.status === 'accepted'))
    .sort((a, b) => (a.legPosition ?? 0) - (b.legPosition ?? 0));
  return cities.length > 1 ? cities : [];
}

/** The slim "Porto · Rui | Lisbon · Ana" strip above a trip with several cities. */
function CitySwitcher({ options, activeMatchId, onPick }: { options: TripOption[]; activeMatchId: string | undefined; onPick: (matchId: string) => void }) {
  return (
    <nav aria-label="Cities of this trip" className="shrink-0 flex gap-2 overflow-x-auto px-4 py-2 border-b" style={{ borderColor: 'var(--line)' }}>
      {options.map((o) => {
        const active = o.matchId === activeMatchId;
        return (
          <button
            key={o.matchId}
            type="button"
            aria-current={active ? 'true' : undefined}
            onClick={() => { if (!active) onPick(o.matchId); }}
            className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium sk-tall"
            style={{
              background: active ? 'var(--accent)' : 'var(--chip-bg)',
              color: active ? 'var(--on-accent)' : 'var(--chip-text)',
            }}
          >
            {o.legCity}{o.hostFirst ? ` · ${o.hostFirst}` : ''}
          </button>
        );
      })}
    </nav>
  );
}

// This is the BOOKING status (did the host accept this trip request?), not
// the handbook's own build/approval status shown by "Approve handbook" on
// the page itself — a bare "Confirmed" here read as if the whole trip,
// handbook included, were already approved.
const STATUS_LABEL: Record<string, string> = {
  accepted: 'Booking confirmed',
  completed: 'Completed',
  proposed: 'Proposed',
  pending: 'Pending',
};

const STATUS_DOT: Record<string, 'success' | 'gold' | 'violet'> = {
  accepted: 'success',
  completed: 'success',
  proposed: 'violet',
  pending: 'gold',
};

const NUMBER_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

function statusDotClass(status: MatchStatus): string {
  const tone = STATUS_DOT[status];
  return tone === 'success' ? 'bg-success' : tone === 'violet' ? 'bg-violet' : 'bg-gold';
}

function formatDateRange(dates: string[]): string | null {
  const valid = dates.filter(Boolean).sort();
  if (valid.length === 0) return null;
  const first = new Date(valid[0] + 'T12:00:00');
  const last = new Date(valid[valid.length - 1] + 'T12:00:00');
  const fm = first.toLocaleDateString('en', { month: 'short' });
  const lm = last.toLocaleDateString('en', { month: 'short' });
  if (valid.length === 1 || first.getTime() === last.getTime()) return `${fm} ${first.getDate()}`;
  if (fm === lm) return `${fm} ${first.getDate()}–${last.getDate()}`;
  return `${fm} ${first.getDate()} – ${lm} ${last.getDate()}`;
}

/** Last row of both trip switchers: start another trip with Alma. */
function NewTripRow({ onClick, compact }: { onClick: () => void; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`sk-hit flex items-center gap-2 rounded-lg text-left w-full text-app-fg-2 hover:text-app-fg transition-colors ${
        compact ? 'px-1.5 py-2 text-caption hover:bg-app-bg' : 'px-2 py-2 text-footnote hover:bg-app-card'
      }`}
    >
      <i className="ti ti-plus text-[length:calc(13px*var(--text-scale))] shrink-0" />
      <span className="truncate flex-1">Plan a new trip</span>
    </button>
  );
}

/** Trip card that opens the trip switcher: the traveler's other trips (if
 *  any) and "Plan a new trip". */
function TripSwitcherCard({
  matchId, meta, trips, onNavigate, onNewTrip,
}: {
  matchId: string;
  meta: TripMeta;
  trips: TripOption[];
  onNavigate: (matchId: string) => void;
  onNewTrip: () => void;
}) {
  const [open, setOpen] = useState(false);
  const hasMultiple = trips.length > 1;
  const statusLabel = STATUS_LABEL[meta.status] ?? meta.status;
  const range = formatDateRange(meta.dayDates);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left"
        style={{
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10,
          padding: '11px 13px', cursor: 'pointer',
        }}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-serif text-footnote text-app-fg whitespace-nowrap overflow-hidden text-ellipsis">
              {meta.tripName ?? meta.destination ?? 'Your trip'}
            </div>
            <div className="flex items-center gap-1.5 mt-[7px]">
              <span className={`inline-block w-[5px] h-[5px] rounded-full shrink-0 ${statusDotClass(meta.status)}`} />
              <span className="text-caption text-app-fg-2">
                {statusLabel}{range ? ` · ${range}` : ''}
              </span>
            </div>
          </div>
          <i className={`ti ti-chevron-down text-app-muted text-[length:calc(13px*var(--text-scale))] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div
          className="mt-1 flex flex-col p-1"
          style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10 }}
        >
          {hasMultiple && <p className="text-caption text-app-muted font-medium px-2 py-1.5">Switch trip</p>}
          {hasMultiple && trips.map((t) => (
            <button
              key={t.matchId}
              type="button"
              onClick={() => { setOpen(false); onNavigate(t.matchId); }}
              className={`sk-hit flex items-center gap-2 px-2 py-2 rounded-lg text-footnote text-left transition-colors ${
                t.matchId === matchId ? 'bg-app-card text-app-fg' : 'text-app-fg-2 hover:text-app-fg hover:bg-app-card'
              }`}
            >
              <span className={`inline-block w-[5px] h-[5px] rounded-full shrink-0 ${statusDotClass(t.status)}`} />
              <span className="truncate flex-1">{t.label}</span>
              {t.matchId === matchId && <i className="ti ti-check text-success text-[length:calc(13px*var(--text-scale))] shrink-0" />}
            </button>
          ))}
          {hasMultiple && <div className="my-1" style={{ borderTop: '1px solid var(--line)' }} />}
          <NewTripRow onClick={() => { setOpen(false); onNewTrip(); }} />
        </div>
      )}
    </div>
  );
}

/** Compact trip name + status selector for the mobile header — tap opens a
 *  dropdown with trip details and, if the traveler has multiple trips, a
 *  switcher. Replaces the old hamburger-triggered sidebar drawer on mobile. */
function TripHeaderSelector({
  matchId, meta, trips, details, onNavigate, onNewTrip,
}: {
  matchId: string;
  meta: TripMeta;
  trips: TripOption[];
  details: SidebarDetailRow[];
  onNavigate: (matchId: string) => void;
  onNewTrip: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const statusLabel = STATUS_LABEL[meta.status] ?? meta.status;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-center gap-1.5 min-w-0 mx-auto sk-hit"
        style={{ maxWidth: 220 }}
      >
        <span className={`inline-block w-[5px] h-[5px] rounded-full shrink-0 ${statusDotClass(meta.status)}`} />
        <span className="font-serif text-subhead text-app-fg truncate">
          {meta.tripName ?? meta.destination ?? 'Your trip'}
        </span>
        <i className={`ti ti-chevron-down text-app-muted text-[length:calc(11px*var(--text-scale))] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-40"
          style={{ width: 280, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, boxShadow: 'var(--shadow-hover)', padding: 10 }}
        >
          <div className="px-1 pb-2">
            <span className="text-caption text-app-fg-2">{statusLabel}</span>
          </div>
          {details.length > 0 && (
            <div className="px-1 pb-1">
              {details.map((row, i) => (
                <div
                  key={row.label}
                  className="flex items-center gap-2 py-1.5"
                  style={{ borderBottom: i < details.length - 1 ? '1px solid var(--line)' : 'none' }}
                >
                  {row.icon && <i className={`ti ti-${row.icon}`} style={{ color: 'var(--text-dim)', fontSize: 'calc(12px * var(--text-scale))' }} />}
                  <span className="text-caption text-app-fg-2 flex-1">{row.label}</span>
                  <span className="text-caption text-app-fg">{row.value}</span>
                </div>
              ))}
            </div>
          )}
          {trips.length > 1 && (
            <div className="pt-2 mt-1" style={{ borderTop: '1px solid var(--line)' }}>
              <p className="text-caption2 text-app-muted font-medium px-1 py-1">Switch trip</p>
              {trips.map((t) => (
                <button
                  key={t.matchId}
                  type="button"
                  onClick={() => { setOpen(false); onNavigate(t.matchId); }}
                  className={`sk-hit flex items-center gap-2 px-1.5 py-2 rounded-lg text-caption text-left w-full transition-colors ${
                    t.matchId === matchId ? 'bg-app-bg text-app-fg' : 'text-app-fg-2 hover:text-app-fg hover:bg-app-bg'
                  }`}
                >
                  <span className={`inline-block w-[5px] h-[5px] rounded-full shrink-0 ${statusDotClass(t.status)}`} />
                  <span className="truncate flex-1">{t.label}</span>
                  {t.matchId === matchId && <i className="ti ti-check text-success text-[length:calc(12px*var(--text-scale))] shrink-0" />}
                </button>
              ))}
            </div>
          )}
          <div className="pt-1 mt-1" style={{ borderTop: '1px solid var(--line)' }}>
            <NewTripRow compact onClick={() => { setOpen(false); onNewTrip(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

interface TripLayoutProps {
  children: React.ReactNode;
  fullBleed?: boolean;
}

export function TripLayout({ children, fullBleed }: TripLayoutProps) {
  const { id: matchId } = useParams<{ id: string }>();
  const { user, activeRole } = useAuth();
  // `toggle` is a stateless action — safe to call from any useTheme()
  // instance. The icon below must NOT read this instance's own `theme`
  // state: AccountMenu (rendered further down, as footerSlot/in the "more"
  // sheet) toggles the theme through its own separate useTheme() call,
  // which this instance never hears about — see the staleness note on
  // useIsDarkTheme in useTheme.ts.
  const { toggle: toggleTheme } = useTheme();
  const isDarkTheme = useIsDarkTheme();
  const { width } = useContentWidth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [meta, setMeta] = useState<TripMeta | null>(null);
  const [trips, setTrips] = useState<TripOption[]>([]);
  const [lastMessage, setLastMessage] = useState<Message | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data: matchRows } = await supabase
        .from('matches')
        .select('id, host_id, portrait_id, status, leg:trip_legs(city, position)')
        .eq('traveler_id', user.id)
        // Not 'proposed': a shortlist with no host chosen or paid isn't a trip yet;
        // it lives on the host choice (/matches).
        .in('status', ['pending', 'accepted'])
        .order('created_at', { ascending: false });

      if (!matchRows || matchRows.length === 0) { setTrips([]); return; }

      const ids = matchRows.map((m) => m.id as string);
      const hostIds = [...new Set(matchRows.map((m) => m.host_id as string))];
      const portraitIds = [...new Set(matchRows.map((m) => m.portrait_id as string).filter(Boolean))];

      const [{ data: names }, { data: itins }, { data: portraitRows }] = await Promise.all([
        supabase.from('profiles').select('id, full_name').in('id', hostIds),
        supabase.from('itineraries').select('match_id, content').in('match_id', ids),
        portraitIds.length > 0
          ? supabase.from('taste_portraits').select('id, trip_name').in('id', portraitIds)
          : Promise.resolve({ data: [] }),
      ]);

      const nameMap = new Map((names ?? []).map((n) => [n.id as string, n.full_name as string | null]));
      const itinMap = new Map((itins ?? []).map((it) => [it.match_id as string, it.content as Record<string, unknown> | null]));
      const portraitNameMap = new Map((portraitRows ?? []).map((p) => [p.id as string, p.trip_name as string | null]));

      setTrips(matchRows.map((m) => {
        const content = itinMap.get(m.id as string) ?? null;
        const tripName = (content ? String(content.trip_name ?? '') || null : null) ?? (m.portrait_id ? portraitNameMap.get(m.portrait_id as string) ?? null : null);
        const destination = content ? String(content.destination ?? '') || null : null;
        const hostFirst = nameMap.get(m.host_id as string)?.split(' ')[0] ?? 'host';
        // A forward foreign key embeds as one object (typed as a list by the client).
        const legRaw = (m as { leg?: unknown }).leg;
        const leg = (Array.isArray(legRaw) ? legRaw[0] : legRaw) as { city: string; position: number } | null | undefined;
        const base = tripName ?? destination ?? `Trip with ${hostFirst}`;
        return {
          matchId: m.id as string,
          status: m.status as MatchStatus,
          // A trip with several cities lists one entry per city, so say which.
          label: leg ? `${base} · ${leg.city}` : base,
          portraitId: (m.portrait_id as string | null) ?? null,
          legCity: leg?.city ?? null,
          legPosition: leg?.position ?? null,
          hostFirst,
        };
      }));
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select(`
          host_id,
          status,
          host_profile:profiles!matches_host_id_fkey(full_name),
          itinerary:itineraries(content),
          portrait:taste_portraits(trip_name),
          conversation:conversations(id, messages)
        `)
        .eq('id', matchId)
        .eq('traveler_id', user.id)
        .maybeSingle();

      if (!match) return;
      const raw = match as Record<string, unknown>;
      // Not chosen and paid yet: there is no trip, chat or host here. Finish the
      // host choice and payment instead of showing a trip "waiting" on a host who
      // was never asked.
      if (raw.status === 'proposed') { navigate('/matches', { replace: true }); return; }
      const hp = raw.host_profile as { full_name: string | null } | null;
      const convRows = raw.conversation as Array<{ id: string; messages: Message[] }> | null;
      const conv = convRows?.[0] ?? null;
      // itineraries.match_id carries a `unique` constraint, so PostgREST
      // treats this as a to-one relationship too and can embed it as a
      // bare object rather than an array — embedOne() handles either shape.
      const content = embedOne(raw.itinerary as { content: Record<string, unknown> } | { content: Record<string, unknown> }[] | null)?.content as Record<string, unknown> | null;
      // matches.portrait_id -> taste_portraits.id is a forward FK (many-to-one
      // from matches), so PostgREST embeds it as a single object — not an
      // array like the itinerary embed above (which is the reverse relation).
      // Indexing it as portraits?.[0] silently always returned undefined,
      // which is why the trip name fell all the way back to "Your trip".
      const portrait = raw.portrait as { trip_name: string | null } | null;
      const days = (content?.days as Array<{ date?: string }> | undefined) ?? [];

      setMeta({
        status: raw.status as MatchStatus,
        hostName: hp?.full_name ?? null,
        tripName: (content ? String(content.trip_name ?? '') || null : null) ?? portrait?.trip_name ?? null,
        destination: content ? String(content.destination ?? '') || null : null,
        dayDates: days.map((d) => d.date ?? '').filter(Boolean),
        hostId: (raw.host_id as string) ?? null,
        conversationId: conv?.id ?? null,
      });
      const msgs = conv?.messages ?? [];
      setLastMessage(msgs.length > 0 ? msgs[msgs.length - 1] : null);
    })();
  }, [user, matchId, navigate]);

  // Keeps the floating tab bar's Chat unread dot live even when the
  // traveler is on Handbook/Profile. Uses its own channel topic — TripChat
  // subscribes to `conv:<id>` for the same row while it's open, and Supabase
  // realtime rejects a second `.on()`/`.subscribe()` pair sharing one topic.
  useEffect(() => {
    if (!meta?.conversationId) return;
    const channel = supabase
      .channel(`conv-nav:${meta.conversationId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'conversations',
        filter: `id=eq.${meta.conversationId}`,
      }, (payload) => {
        const updated = payload.new as { messages: Message[] };
        setLastMessage(updated.messages.length > 0 ? updated.messages[updated.messages.length - 1] : null);
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [meta?.conversationId]);

  const activeKey =
    pathname.endsWith('/chat') ? 'chat' :
    pathname.endsWith('/profile') ? 'profile' : 'handbook';

  // Chat counts as seen while it's the open tab; markConversationSeen also
  // runs from TripChat itself, so this stays correct if that page unmounts.
  const isOnChat = activeKey === 'chat';
  useEffect(() => {
    if (meta?.conversationId && isOnChat) markConversationSeen(meta.conversationId);
  }, [meta?.conversationId, lastMessage, isOnChat]);

  if (!matchId) return null;

  const hostFirst = meta?.hostName?.split(' ')[0] ?? 'Host';
  const citySwitchers = citySwitchOptions(trips, matchId);

  const chatUnread = !isOnChat && !!meta?.conversationId && !!user
    ? isConversationUnread(meta.conversationId, lastMessage, user.id)
    : false;

  const navItems: SidebarNavItem[] = [
    { key: 'handbook', icon: 'clipboard-list', label: 'Handbook', onClick: () => navigate(`/trip/${matchId}/handbook`) },
    { key: 'chat', icon: 'message-circle', label: `Chat with ${hostFirst}`, onClick: () => navigate(`/trip/${matchId}/chat`) },
    { key: 'profile', icon: 'user', label: `${hostFirst}'s profile`, onClick: () => navigate(`/trip/${matchId}/profile`) },
  ];

  // A new trip always starts a fresh chat with Alma: any unfinished draft
  // from an earlier attempt is discarded first (the chat restores from it
  // on open).
  const planNewTrip = () => { clearPendingTryChat(); navigate('/try'); };

  const footerItems: SidebarNavItem[] = [
    { key: 'new-trip', icon: 'plus', label: 'Plan a new trip', onClick: planNewTrip },
    { key: 'faq', icon: 'help-circle', label: 'FAQ', onClick: () => setShowFaq(true) },
    { key: 'support', icon: 'headset', label: 'Support', onClick: () => setShowSupport(true) },
    { key: 'notifications', icon: 'bell', label: 'Notifications', onClick: () => setShowNotifications((v) => !v) },
    { key: 'feedback', icon: 'message-2', label: 'Feedback', onClick: () => setShowFeedback((v) => !v) },
  ];

  const details: SidebarDetailRow[] = [];
  if (meta?.destination) details.push({ icon: 'map-pin', label: 'City', value: meta.destination });
  const range = meta ? formatDateRange(meta.dayDates) : null;
  if (range) details.push({ icon: 'calendar-event', label: 'Dates', value: range });
  if (meta && meta.dayDates.length > 0) {
    details.push({ icon: 'sun', label: 'Days', value: NUMBER_WORDS[meta.dayDates.length] ?? String(meta.dayDates.length) });
  }
  if (meta?.hostName) details.push({ icon: 'user-heart', label: 'Host', value: hostFirst });

  const themeIcon = isDarkTheme ? 'sun' : 'moon';

  const sidebarContent = (
    <Sidebar
      items={navItems}
      footerItems={footerItems}
      active={activeKey}
      groupLabel={meta?.tripName ?? meta?.destination ? 'Your trip' : undefined}
      details={details}
      detailsLabel="Trip details"
      headerActions={
        <>
          <IconButton icon={themeIcon} label="Toggle theme" onClick={toggleTheme} />
          <span className="hidden md:block">
            <IconButton
              icon="layout-sidebar-left-collapse"
              label="Collapse sidebar"
              onClick={() => setCollapsed(true)}
            />
          </span>
        </>
      }
      headerSlot={meta ? (
        <div style={{ marginBottom: 20 }}>
          <TripSwitcherCard
            matchId={matchId}
            meta={meta}
            trips={trips}
            onNavigate={(id) => navigate(`/trip/${id}/handbook`)}
            onNewTrip={planNewTrip}
          />
        </div>
      ) : undefined}
      footerSlot={<AccountMenu variant="quiet" />}
      style={{ height: '100%', width: '100%', border: 'none', borderRight: '1px solid var(--line)' }}
    />
  );

  const tabItems: FloatingTabBarItem[] = [
    { key: 'chat', icon: 'message-circle', label: 'Chat', onClick: () => navigate(`/trip/${matchId}/chat`), dot: chatUnread },
    { key: 'handbook', icon: 'clipboard-list', label: 'Handbook', onClick: () => navigate(`/trip/${matchId}/handbook`) },
    { key: 'profile', icon: 'user', label: hostFirst, onClick: () => navigate(`/trip/${matchId}/profile`) },
    { key: 'more', icon: 'dots', label: 'More', onClick: () => setMoreOpen(true) },
  ];

  return (
    <div className="flex h-dvh bg-app-bg">
      {/* Sidebar — desktop only; mobile uses the trip selector + floating tab bar below */}
      <div
        ref={sidebarRef}
        className={`
          hidden md:sticky md:top-0 md:h-dvh pl-safe
          ${collapsed ? 'md:hidden' : 'md:block md:w-[252px]'}
        `}
        style={{ background: 'var(--sidebar-bg)' }}
      >
        {sidebarContent}
      </div>

      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Expand button when collapsed (desktop) */}
        {collapsed && (
          <div className="hidden md:block absolute top-[22px] left-4 z-30">
            <IconButton
              icon="layout-sidebar-left-expand"
              label="Expand sidebar"
              onClick={() => setCollapsed(false)}
            />
          </div>
        )}

        {/* Mobile header — trip selector only (replaces the old hamburger
            drawer). No logo or theme toggle — deliberately bare per design. */}
        <header
          className="md:hidden sticky top-0 z-30 shrink-0 pt-safe px-safe border-b bg-app-bg"
          style={{ borderColor: 'var(--line)' }}
        >
          <div className="flex items-center justify-center w-full px-4 py-[13px]">
            {meta ? (
              <TripHeaderSelector
                matchId={matchId}
                meta={meta}
                trips={trips}
                details={details}
                onNavigate={(id) => navigate(`/trip/${id}/handbook`)}
            onNewTrip={planNewTrip}
              />
            ) : <div />}
          </div>
        </header>

        {citySwitchers.length > 0 && (
          <CitySwitcher
            options={citySwitchers}
            activeMatchId={matchId}
            onPick={(id) => navigate(pathname.replace(matchId ?? '', id))}
          />
        )}

        {fullBleed ? (
          <main className="flex-1 min-h-0 flex flex-col">{children}</main>
        ) : (
          <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className={`${WIDTH_CLASSES[width]} mx-auto w-full`}>
              {children}
            </div>
          </main>
        )}

        {/* Mobile floating tab bar — primary trip nav. On Handbook/Profile it
            sits below scrollable content, so it gets the same 40px fade-out
            overlay as the chat header (TripChat.tsx), inverted top-to-bottom.
            On Chat it docks directly below the composer, which already owns
            its own fade above the message list — a second overlay here would
            just wash out the disclaimer text, so it's skipped there. The pill
            itself stays solid either way, that's its card style. */}
        <KeyboardCollapse className="md:hidden shrink-0 relative">
          {!fullBleed && (
            <div
              className="absolute left-0 right-0 bottom-full h-10 pointer-events-none z-[1]"
              style={{ background: 'linear-gradient(to bottom, transparent, var(--bg))' }}
            />
          )}
          <FloatingTabBar items={tabItems} active={activeKey} />
        </KeyboardCollapse>
      </div>

      {moreOpen && (
        <BottomSheet title="More" onClose={() => setMoreOpen(false)}>
          <div className="flex flex-col gap-0.5">
            {footerItems.map((item) => (
              <NavItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                onClick={() => { item.onClick?.(); setMoreOpen(false); }}
              />
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', margin: '10px 8px' }} />
          <div className="px-2 pb-1">
            <AccountMenu variant="quiet" onNavigate={() => setMoreOpen(false)} />
          </div>
        </BottomSheet>
      )}

      {showFaq && <FaqModal audience={activeRole === 'admin' ? 'host' : activeRole} onClose={() => setShowFaq(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
      {showFeedback && <FeedbackPopover anchorRef={sidebarRef} onClose={() => setShowFeedback(false)} />}
      {showNotifications && <NotificationsPopover anchorRef={sidebarRef} onClose={() => setShowNotifications(false)} />}
    </div>
  );
}

export default TripLayout;
